import * as React from 'react';
import { Box, Typography } from '@mui/material';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import { useTranslation } from 'react-i18next';
import { HOUR_HEIGHT, CALENDAR_NOTE_CONFIG } from './calendarConstants';
import useCalendarState from './hooks/useCalendarState';
import DraggableEvent from './DraggableEvent';
import { CalendarNoteTooltipContent } from './EventTooltipContent';

// Format hour to HH:MM string
const formatHour = (hour) => {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

function CalendarNoteEvent({ note, startHour: workStartHour = 8, endHour: workEndHour = 16, dayIndex, overlapIndex = 0, overlapTotal = 1 }) {
  const { t } = useTranslation();
  const { openEditNoteDialog } = useCalendarState();

  const eventStartHour = note._clampedStartHour;
  const eventEndHour = note._clampedEndHour;
  const durationHours = eventEndHour - eventStartHour;

  if (durationHours <= 0) {
    return null;
  }

  const top = (eventStartHour - workStartHour) * HOUR_HEIGHT + 2;
  const height = Math.max(durationHours * HOUR_HEIGHT - 4, 20);

  const config = CALENDAR_NOTE_CONFIG;

  // Format timespan using original (unclamped) hours
  const timespan = `${formatHour(note.local_start_hour)} - ${formatHour(note.local_end_hour)}`;

  const handleClick = (e) => {
    e.stopPropagation();
    openEditNoteDialog(note);
  };

  const tooltipContent = (
    <CalendarNoteTooltipContent config={config} timespan={timespan} createdByName={note.created_by_name} content={note.content} />
  );

  return (
    <DraggableEvent
      event={note}
      eventType="calendar_note"
      top={top}
      height={height}
      backgroundColor={config.bg}
      borderColor={config.color}
      onClick={handleClick}
      dayIndex={dayIndex}
      gridStartHour={workStartHour}
      gridEndHour={workEndHour}
      overlapIndex={overlapIndex}
      overlapTotal={overlapTotal}
      tooltipContent={tooltipContent}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, overflow: 'hidden', flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <StickyNote2Icon sx={{ fontSize: 14, color: config.color, flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 500,
              color: config.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {t('Note')}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '10px',
            color: config.text,
            opacity: 0.8,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {timespan}
          {note.employee_name
            ? ` \u2022 ${note.employee_name}`
            : note.created_by_name && ` \u2022 ${note.created_by_name}`}
        </Typography>
        <Typography
          sx={{
            fontSize: '11px',
            color: config.text,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflow: 'hidden',
            flex: 1,
          }}
        >
          {note.content}
        </Typography>
      </Box>
    </DraggableEvent>
  );
}

export default CalendarNoteEvent;
